import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { AgGridAngular, AngularFrameworkComponentWrapper, AngularFrameworkOverrides } from 'ag-grid-angular';
import { CellClickedEvent, ColDef, ColGroupDef, Column, RowNode, RowSelectedEvent, SelectionChangedEvent, SuppressKeyboardEventParams } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { WizDatasource } from './../../../services/collections/wiz-datasource';
import { WizDndManager } from './../../../services/dragndrop/wiz-dnd.manager';
import { WizRoot } from './../../views/wiz-root';
import { DND_DATA_ID, GridDNDConfig, GridDNDData } from './support/wiz-ag-grid.dnd.config';
import { CellCheckedEvent } from './wiz-ag-grid.model';
import { GroupHeaderRenderer } from './groupheader.renderer';
import { IWIZ_COMPONENT, IWIZComponent} from '../../../interfaces/iwiz-component';
import { VALUE_SPACE, WDSChanges, WDSSelectionMode, WIZ_EVENT_TYPE, WizBooleanUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../events/wiz-event';
import { WizEventManager } from '../../../events/wiz-event.manager';
import { WizTranslationManager } from '../../../services/translation/wiz-translation.manager';

@Component({
  selector: 'wiz-ag-grid',
  template: '',
  styleUrls: ['./wiz-ag-grid.component.scss'],
  providers: [
    AngularFrameworkOverrides,
    AngularFrameworkComponentWrapper,
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizAgGrid) },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WizAgGrid<T> extends AgGridAngular implements OnInit, OnChanges, OnDestroy, AfterViewInit, IWIZComponent {
  get _initialized(): boolean {
    return this[`_initialised`];
  }

  private _colDeletion: ColDef = null; // colonne de suppression avec la poubelle

  protected get coldDeletion(): ColDef {
    // console.log('get colDeletion', this);
    return (
      this._colDeletion ||
      (this._colDeletion = {
        // spécifique delete, renderer custom
        field: 'DEL', // forcer le nom de champ
        pinned: 'left', // forcer le pin, cette colonne n'est pas dans la popUp de conf
        resizable: false,
        enableCellChangeFlash: false,
        width: 30,
        cellRenderer: this.deletionCellRenderer.bind(this),
        // cellClass: this.deletionCellClass.bind(this),
        headerName: '', // pas de libellé pour le header
        onCellClicked: (event: CellClickedEvent) => this.wizDeleteItem.emit(event),
      })
    );
  }

  /** class Func de rendu des icones de poubelle pour la suppression */
  private deletionCellRenderer(params: any): any {
    if(!!this?._customDeletionRenderer) {
      // console.log('_customDeletionRenderer', params);
      return this.customDeletionRenderer(params);
    }
    // console.log('del simple', params);
    return `<div class="wiz-cell-container"><i class="deletion-grid-cell"></i></div>`;
  }


  /** injection du widget pour prise de la conf */
  private _widget: any = null;
  @Input()
  protected set widget(widget: any)  {
    // console.log('set customDeletion');
    this._colDeletion = null; // forcer le refresh de la colonne de suppression
    this._widget = widget;
  }
  protected get widget(): any {
    return this._widget;
  }

  /** injection d'un rendu custom pour l'icone de suppression */
  private _customDeletionRenderer: Function = null;
  
  @Input()
  protected set customDeletionRenderer(func: Function)  {
    // console.log('set customDeletion');
    this._colDeletion = null; // forcer le refresh de la colonne de suppression
    this._customDeletionRenderer = func;
  }
  protected get customDeletionRenderer(): Function {
    return this._customDeletionRenderer;
  }

  /** injection d'un rendu custom pour l'icone de suppression */
  private _customRowGroupRenderer: Function = null;
  
  @Input()
  protected set customRowGroupRenderer(func: Function)  {
    // console.log('set customDeletion');
    this._colGroup = null; // forcer le refresh de la colonne de suppression
    this._customRowGroupRenderer = func;
  }
  protected get customRowGroupRenderer(): Function {
    return this._customRowGroupRenderer;
  }


  private _colGroup: ColDef; // colonne de sélection avec les checkboxes
  protected get colGroup(): ColDef {
    return (
      this._colGroup ||
      (this._colGroup = {
        // spécifique select, renderer custom
        field: 'GRP', // forcer le nom de champ
        pinned: 'left', // forcer le pin, cette colonne n'est pas dans la popUp de conf
        resizable: false,
        enableCellChangeFlash: false,
        width: 30,
        cellRenderer: this.rowGroupRenderer.bind(this),
        valueGetter: this.rowGroupValue.bind(this),
        // cellClass: this.deletionCellClass.bind(this),
        // headerName: '', // pas de libellé pour le header
        headerComponentFramework: GroupHeaderRenderer, // rendu unique des headers
        headerComponentParams : { grid: this }, // passage du grid en params
        onCellClicked: (event: CellClickedEvent) => this.wizGroupClicked.emit(event),
      })
    );
  }

  /** accesseur sur la valeur d'un élément de rowGroup */
  private rowGroupValue(params: any): any {
    let result: number = 0;
    let isSub: boolean = !!params.data?.isSubTotalLine;
    if(isSub) {
      result = 1;
    }

    return result;
  }

  /** class Func de rendu des icones de poubelle pour la suppression */
  private rowGroupRenderer(params: any): any {
    if(!!this?._customRowGroupRenderer) {
      // console.log('_customDeletionRenderer', params);
      return this.customRowGroupRenderer(params);
    }
    return `<div class="wiz-cell-container"><i class="inner-group-icon"></i></div>`;
  }

  private _colSelection: ColDef; // colonne de sélection avec les checkboxes
  protected get colSelection(): ColDef {
    return (
      this._colSelection ||
      (this._colSelection = {
        // spécifique select, renderer custom
        field: 'SEL', // forcer le nom de champ
        pinned: 'left', // forcer le pin, cette colonne n'est pas dans la popUp de conf
        resizable: false,
        enableCellChangeFlash: false,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        width: 30,
        
        checkboxSelection: true,
        headerName: '', // pas de libellé pour le header
      })
    );
  }

  private _colSelectionSingle: ColDef; // colonne de sélection avec les checkboxes, mode mono
  protected get colSelectionSingle(): ColDef {
    return (
      this._colSelectionSingle ||
      (this._colSelectionSingle = {
        // spécifique select, renderer custom
        field: 'SEL', // forcer le nom de champ
        pinned: 'left', // forcer le pin, cette colonne n'est pas dans la popUp de conf
        resizable: false,
        enableCellChangeFlash: false,
        headerCheckboxSelection: false,
        headerCheckboxSelectionFilteredOnly: false,
        width: 30,
        checkboxSelection: true,
        headerName: '', // pas de libellé pour le header
      })
    );
  }

  @Input()
  public wizShowDelete: boolean = false;

  /** mode de sélection hérité de la DS (TODO ne pas avoir à redéfinir) */
  @Input()
  public wizRowSelection: WDSSelectionMode = 'single';

  /** le wiz-single, c'est uniquement un patch pour forcer les checks */
  public get niceRowSelection(): WDSSelectionMode {
    if(this.wizRowSelection === 'wiz-single') {
      return 'single';
    }
    return this.wizRowSelection;
  }

  @Input()
  public wizShowGroup: boolean = false;




  /** Permet d'utiliser une wizDatasource avec l'ag-grid plutôt qu'un array. */
  @Input()
  wizDatasource: WizDatasource<T>;

  @Output()
  wizDeleteItem: EventEmitter<CellClickedEvent> = new EventEmitter();

  @Output()
  wizCellClicked: EventEmitter<CellClickedEvent> = new EventEmitter();

  @Output()
  wizCellEdit: EventEmitter<CellClickedEvent> = new EventEmitter();

  @Output()
  wizCellChecked: EventEmitter<CellCheckedEvent> = new EventEmitter();

  @Output()
  wizActionClicked: EventEmitter<CellClickedEvent> = new EventEmitter();
  
  @Output()
  wizImageClicked: EventEmitter<CellClickedEvent> = new EventEmitter();

  @Output()
  wizGroupClicked: EventEmitter<CellClickedEvent> = new EventEmitter();


  private _sub$: Subscription = new Subscription();

  private _dndConfig: GridDNDConfig<T>;

  /**
   * Configuration du drag and drop
   */
  @Input()
  public set dndConfig(pConfig: GridDNDConfig<T>) {
    if (this._dndConfig) {
      // on fait le diff pour savoir quels events on doit supprimer
      if (this._dndConfig.dropEnabled && (!pConfig || !pConfig.dropEnabled) && !this.wizDisabled) {
        WizEventManager.removeListener(this, this, 'dragover');
        WizEventManager.removeListener(this, this, 'drop');
      }
    }

    this._dndConfig = pConfig;

    if (this._dndConfig) {
      if (this._initialized) {
        this.handleDragColumns();
      }

      if (this._dndConfig.dropEnabled && !this.wizDisabled) {
        WizEventManager.addListener(this, this, 'dragover', this.onDragOver);
        WizEventManager.addListener(this, this, 'drop', this.onDrop);
       //  this.gridOptions.onRowDragEnd = this.dropMe.bind(this);
      }
    }
  }

  constructor(
    public elementRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef,
    viewContainerRef: ViewContainerRef,
    angularFrameworkOverrides: AngularFrameworkOverrides,
    frameworkComponentWrapper: AngularFrameworkComponentWrapper,
    componentFactoryResolver: ComponentFactoryResolver
  ) {
    
    super(elementRef, viewContainerRef, angularFrameworkOverrides, frameworkComponentWrapper, componentFactoryResolver);
    this.suppressKeyboardEvent = this.shouldSuppressKeyboardevent.bind(this);
  }

  ngOnChanges(changes: SimpleChanges) {
    let columnsChanged: boolean = false;
    if (changes.wizDatasource) {
      this._onDataSourceChanged();
    }
    if (changes.wizRowSelection) {
      this._onWizRowSelection();
      columnsChanged = true;
    }
    if (changes.columnDefs) {
      columnsChanged = true;
    }
    if (changes.wizShowDelete) {
      columnsChanged = true;
    }
    if (changes.wizShowGroup) {
      columnsChanged = true;
    }

    if (columnsChanged) {
      this._onColumnsChanged(changes);
    }

    super.ngOnChanges(changes);
  }

  private _onWizRowSelection() {
    if (this.wizDatasource) {
      this.wizDatasource.setSelectionMode(this.niceRowSelection);
      // console.log('ag select ', this.wizRowSelection, this.wizDatasource.selectionMode);
    }
    // la row selection native de l'ag-grid est forcée sur multiple, car il est impossible
    // de la changer programmatiquement.
    const innerRowSelection: string = 'multiple';
    if (this.gridOptions) {
      this.gridOptions = {
        ...this.gridOptions,
        rowSelection: innerRowSelection,
      };
    }
    this.rowSelection = innerRowSelection;
  }

  /** 
   * ajout récursif de la méthode de cellClick 
   * 
   * @param groupColFrom la colonne dont on va prendre les enfants
   * @param groupColTo la colonne cible pour ajout des enfants
   */
  private _recurseGroupedCols (groupColFrom: ColGroupDef, groupColTo: ColGroupDef)  {  
    groupColFrom.children?.forEach((colDef: any) => {
      if (!!colDef.groupId) { // c'est un group
        const newGp: any = {...colDef}; // on perd l'instance
        newGp.children= []; // on rince les enfants
        groupColTo.children.push(newGp);
        this._recurseGroupedCols(colDef, newGp); // on alimente les enfants en récursif
      } else {
        if (this._isSystemColumn(colDef)) {
          groupColTo.children.push(colDef);
        } else  {
          groupColTo.children.push({...colDef, onCellClicked: (params) => this._onCellClicked(params) });
        }
      }
    });
  }

  /** sur changement de définition de colonnes... */
  private _onColumnsChanged(changes: SimpleChanges) {
    if (this.columnDefs) {
      //... prendre en compte les groupes...
      let computedColumns: any[] = [];
      this.columnDefs.forEach((colDef: any) => {
        if (!!colDef.groupId) { // c'est un group
          const newGp: any = {...colDef}; // on perd l'instance
          newGp.children= []; // on rince les enfants
          computedColumns.push(newGp);

          this._recurseGroupedCols(colDef, newGp); // on alimente les enfants en récursif
        } else {
          if (this._isSystemColumn(colDef)) {
            computedColumns.push(colDef);
          } else  {
            computedColumns.push({...colDef, onCellClicked: (params) => this._onCellClicked(params) }); // ajout de la fonction cellClick
          }
        }

      });

      // ajout de la colonne de groupe de lignes
      if (this.wizShowGroup) {
        computedColumns = computedColumns.filter((colDef) => this.colGroup !== colDef);
        computedColumns = [this.colGroup, ...computedColumns];
        // if (computedColumns.indexOf(this.colGroup) === -1) {
        //   computedColumns = [this.colGroup, ...computedColumns];
        // }
      } else {
        computedColumns = computedColumns.filter((colDef) => this.colGroup !== colDef);
      }

      // ajout de la colonne de sélection multiple
      if (this.wizRowSelection === 'multiple') {
        computedColumns = computedColumns.filter((colDef) => this.colSelection !== colDef);
        computedColumns = [this.colSelection, ...computedColumns];
        // if (computedColumns.indexOf(this.colSelection) === -1) {
        //   computedColumns = [this.colSelection, ...computedColumns];
        // }
      } else {
        computedColumns = computedColumns.filter((colDef) => this.colSelection !== colDef);
      }

      // ajout de la colonne de sélection single (pas le header)
      if (this.wizRowSelection === 'wiz-single') {
        computedColumns = computedColumns.filter((colDef) => this.colSelectionSingle !== colDef);
        computedColumns = [this.colSelectionSingle, ...computedColumns];
        // if (computedColumns.indexOf(this.colSelectionSingle) === -1) {
        //   computedColumns = [this.colSelectionSingle, ...computedColumns];
        // }
      } else {
        computedColumns = computedColumns.filter((colDef) => this.colSelectionSingle !== colDef);
      }     

      // ajout de la colonne de suppression
      if (this.wizShowDelete) {
        computedColumns = computedColumns.filter((colDef) => this.coldDeletion !== colDef);
        computedColumns = [this.coldDeletion, ...computedColumns];
        // if (computedColumns.indexOf(this.coldDeletion) === -1) {
        //   computedColumns = [this.coldDeletion, ...computedColumns];
        // }
      } else {
        computedColumns = computedColumns.filter((colDef) => this.coldDeletion !== colDef);
      }
 

      // affectation de la traduction sur les headers
      this.setHeaderValueGetter(computedColumns);

      if (this._initialized) {
        // gestion du cas ou j'ai déjà un changement de columnDefs à appliquer par AGGrid dans ce cas je modifie la définition dans les changes
        // cela évite un double dessin des colonnes et on gagne en performances
        if (!!changes.columnDefs) {
          changes.columnDefs = new SimpleChange(changes.columnDefs.previousValue, computedColumns, changes.columnDefs.firstChange);
        } else {
          this.api.setColumnDefs(computedColumns);
        }
      } else {
        this.columnDefs = computedColumns;
      }
    }

    if(this.widget?.context?.hsty) {
      this.setHeaderDefaultColor();
    }
    
  }

  private setHeaderDefaultColor(): void {
    if(this.widget?.context?.hsty) {
      const styleParts: string[] = this.widget.context.hsty.split('|') || [];
      const headerViewport = this?.elementRef?.nativeElement
          .querySelector('.ag-header-viewport') as HTMLElement;
        if (headerViewport) {
          headerViewport.style.backgroundColor = styleParts[0];
          headerViewport.style.color = styleParts[1];
        }
      const headerPinned = this?.elementRef?.nativeElement
          .querySelector('.ag-pinned-left-header') as HTMLElement;
        if (headerPinned) {
          headerPinned.style.backgroundColor = styleParts[0];
          headerPinned.style.color = styleParts[1];
        }
      //console.log('aggrid column def', this.widget?.context?.fdk);
    }
  
  }

  private _onCellClicked(params: CellClickedEvent) {
    // Les éléments spécifiques du grid (bouton d'édition, che)
    const tokenList: DOMTokenList = (params.event.target as Element)?.classList;
    if (params.colDef.editable) {
      if( tokenList.contains('wiz-cell-bool')) { // basé sur la classe de la cellule, checkbox
        this.wizCellChecked.emit({...params, newValue: WizBooleanUtils.nextValue(params.value) });
      } else if (tokenList.contains('wiz-cell-image')) { // clic sur une image
        let blobid: string = (params.event?.target as HTMLElement)?.getAttribute('blobid');
        // console.log('params', blobid);
        params.value = blobid;;
        this.wizImageClicked.emit({...params });
      }
      else if (tokenList.contains('cell-edit-icon')) { // Cas du clic sur l'icone d'édition
        this.wizCellEdit.emit(params);
      } else { // clic simple sur une cellule
        this.wizCellClicked.emit(params);
      }
    } 

    else if (tokenList.contains('wiz-cell-action')) { // gestion des renderers de type action (connect)
      this.wizActionClicked.emit({...params });
      // console.log('_onCellClicked', params);
    }
    else if (tokenList.contains('wiz-cell-file')) { // gestion du téléchargement des fichiers
      this.wizCellEdit.emit({...params });
      // console.log('_onCellClicked', params);
    }
    else if (tokenList.contains('wiz-cell-image')) { // clic sur une image
      let blobid: string = (params.event?.target as HTMLElement)?.getAttribute('blobid');
        // console.log('params', blobid);
        params.value = blobid;;
        this.wizImageClicked.emit({...params });
    }
  }

  // les colonnes de suppression ou de sélection
  private _isSystemColumn(pColumn: ColDef): boolean {
    return pColumn === this.colSelection || 
    pColumn === this.colSelectionSingle || 
    pColumn === this.coldDeletion || 
    pColumn === this.colGroup;
  }

  /**
   * Gestion des colonnes draggable, on set la callback dndSourceOnRowDrag pour ajouter des données dans le dataTransfer
   */
  private handleDragColumns(): void {
    if (!!this._dndConfig && !!this.columnApi) {
      this.columnApi.getAllColumns().forEach((col: Column) => {
        if (col.getColDef().dndSource) {
          col.getColDef().dndSourceOnRowDrag =
            this._dndConfig.dragEnabled && !this.wizDisabled ? this.onRowDrag.bind(this) : null;
        }
      });
    }
  }

  /**
   * Fonction appelée sur le drag start d'une row
   * @param params Les params de drag
   */
  protected onRowDrag(params: { rowNode: RowNode; dragEvent: DragEvent }): void {
    const { rowNode, dragEvent } = params;

    // donnée pas draggable ?
    if (this._dndConfig.itemDraggable && !this._dndConfig.itemDraggable(rowNode.data)) {
      dragEvent.preventDefault();
      dragEvent.stopPropagation();
      return;
    }

    // data pour enregistrement dans le dnd manager
    const dndData: GridDNDData<T> = {
      data: rowNode.data,
      source: this.wizDatasource,
      sourceConfig: this._dndConfig,
      sourceComponent: this,
    };

    // utilisation du timestamp pour générer l'id
    const dndid: string = `${Date.now()}`;
    dragEvent.dataTransfer.setData(DND_DATA_ID, dndid);

    WizDndManager.setDragData(dndid, dndData);
  }

  /**
   * Drag Over
   * @param event drag event
   */
  protected onDragOver(event: DragEvent): void {
    const dragSupported = event.dataTransfer.types.length;
    if (dragSupported) {
      event.dataTransfer.dropEffect = 'move';
      event.preventDefault();
    }
  }

  protected onDrop(event: DragEvent): void {
    const dragId: string = event.dataTransfer.getData(DND_DATA_ID);
    const dragData: GridDNDData<T> = WizDndManager.getDragData(dragId);

    // gestion de l'acceptation
    if (dragData) {
      event.preventDefault();

      // pas dans le même grid, ce n'est pas un event de reordering
      if (dragData.sourceComponent === this && dragData.sourceConfig.dropAction !== 'reorder') {
        return;
      }

      if (this._dndConfig?.dropAccept && !this._dndConfig.dropAccept(dragData.data)) {
        return;
      }
      
      // reordering
      if (dragData.sourceComponent === this && dragData.sourceConfig.dropAction === 'reorder') {
        let targ: number = null; // on cherche l'index cible
        
        if (targ === null) {
          let baseElement: HTMLElement = event.target as HTMLElement; // le point de contact
          let nodeMap: NamedNodeMap; //  = baseElement.attributes;
          
          while (targ === null && !!baseElement) {
            nodeMap = baseElement.attributes; // la map d'attributs de l'élément, on cherche row-id
            // console.log('nodeMap', nodeMap,baseElement);
            if (nodeMap?.getNamedItem('row-id')) {
              targ = Number(nodeMap.getNamedItem('row-id').value);
              // console.log('nodeMap', nodeMap, nodeMap.getNamedItem('row-id').value); // .getNamedItem('id').value);
            } else {
              baseElement = baseElement.parentElement; // on prend papa
             // console.log('baseElement', baseElement);
            }
            // console.log('parent', parentElem, parentNodeMap);
          }
        }
 
        const targetPosition: number = targ;
        const currentIndex: number = this.wizDatasource.getItemIndex(dragData.data);
        this.wizDatasource.moveItem(currentIndex,targetPosition > currentIndex && targetPosition !== this.wizDatasource.length - 1
            ? targetPosition - 1
            : targetPosition
        );

      } else {
        this.wizDatasource.addItem(dragData.data);

        if (dragData.sourceConfig.dropAction === 'move') {
          dragData.source.removeItem(dragData.data);
        }
      }

      WizEventManager.dispatchEvent(this, new WizEvent(WIZ_EVENT_TYPE.DRAG_DROPPED, this, dragData.data));
    }
  }

  /**
   * Gestion de la datasource (ajout des écouteurs,  mise à jour des noeuds sélectionnés...)
   */
  private _onDataSourceChanged(): void {
    if (this.wizDatasource) {
      this._sub$.add(
        this.wizDatasource.changes
          .pipe(
            filter(({ type }) => type === 'init' || type === 'refreshed'),
            distinctUntilChanged()
          )
          .subscribe((changes: WDSChanges) => {
            this._onDataSourceRefreshed(changes);
            this._changeDetectorRef.markForCheck();
          })
      );

      // on écoute également les changements de sélection
      this._sub$.add(
        this.wizDatasource.changes
          .pipe(filter(({ type }) => type === 'programmaticChanged' || type === 'selectionChanged'))
          .subscribe((changes: WDSChanges) => {
            this.synchronizeSelectionState(changes); // on synchronise d'abord...
            if (changes.type === 'selectionChanged') {
              WizEventManager.dispatchEvent(this, new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGED, this, changes.value));
            }
          })
      );

      // abonnement pour écoute des itemUpdated et donner les nouvelles valeurs aux noeuds
      this._sub$.add(
        this.wizDatasource.changes.pipe(filter(({ type }) => type === 'itemUpdated')).subscribe(({ value }) => {
          const changedRows: any[] = value;
          if (changedRows?.length > 0) {
            this.api.forEachNode((rowNode: RowNode, index: number) => {
              const idx: number = changedRows.indexOf(rowNode.data);
              if (idx > -1) {
                rowNode.updateData(changedRows[idx]);
              }
            });
          }
        })
      );
    }
  }

  private _onDataSourceRefreshed(changes?:WDSChanges) {
    if (this.wizDatasource && this._initialized) {
      // console.log('_onDataSourceRefreshed', this);
      // this.gridOptions.api.setRowData(this.wizDatasource.getWorkedSource());
      if(this.gridOptions?.api) {
        this.gridOptions?.api.setRowData(this.wizDatasource.getWorkedSource().filter(item => this.isIN(item)));
        // perte de la sélection...
        this.synchronizeSelectionState(changes); // gros naze 
        // console.log('ds refreh aggrid OK');
      }

    }
  }

  /**
   * Synchronisation de l'état de sélection du grid DEPUIS la DS
   */
  public synchronizeSelectionState(changes?:WDSChanges): boolean {
    let hasChanged: boolean = false;
    // Si le changement vient du programme ou du refresh de la datasource, il est inutile que l'ag-grid propage l'événement selectionChanged
    // C'est même trés négatif en termes de performances d'affichage, si plusieurs milliers de lignes sont sélectionnées
    let skipEvent: boolean = (changes && (changes.type === 'programmaticChanged' || changes.type === 'refreshed')); 
    if (this.api) {
      if (this.wizDatasource.selectedItems?.length > 0) {
        const selectedItems: Set<T> = new Set(this.wizDatasource.selectedItems);
        this.api.forEachNode((rowNode: RowNode, index: number) => {
          // il est en état sélectionné mais il ne fait plus parti des selectionnés => on passe à false
          if (rowNode.isSelected() && !selectedItems.has(rowNode.data)) {
            rowNode.setSelected(false, false, skipEvent); // skipEvent permet de déclencher (ou pas) l'événement selectionChanged
            hasChanged = true;
          } else if (!rowNode.isSelected() && selectedItems.has(rowNode.data)) {
            // il n'est pas en état sélectionné mais il fait parti des sélectionnés => on passe à true
            rowNode.setSelected(true, false, skipEvent); // skipEvent permet de déclencher (ou pas) l'événement selectionChanged
            hasChanged = true;
          }
        });
      } else {
        if (this.api.getSelectedNodes().length > 0) { // on avait des sélections
          hasChanged = true;
        }
        this.api.deselectAll();
      }
    }
    return hasChanged;
  }

  ngOnInit() {
    WizEventManager.addListener(this, WizRoot.root(), WIZ_EVENT_TYPE.TRANSLATION_LOADED, this.onTranslationLoaded);
    WizEventManager.addListener(this, this, WIZ_EVENT_TYPE.SELECTION_CHANGING, this.onSelectionChanging);
    WizEventManager.addListener(this, this, WIZ_EVENT_TYPE.ROW_GROUP_EXPAND, this.onGroupHeaderExpand);

    this._sub$.add( // écouteur sur grid OK pour préparation du grid
      this.gridReady.asObservable().subscribe(() => {
        this.synchronizeDisabledOnColumns();
        this._onDataSourceRefreshed();
        this.handleDragColumns();
        this.setHeaderDefaultColor();
      })
    );

    this._sub$.add( // écouteur sur rowClick pour sélection ligne par ligne
      this.rowClicked.asObservable().subscribe((value) => {
        if (this._wizDisabled) {
          return;
        }

        // On se branche sur le sélection changed pour emit un wiz event et mettre à jour la datasource
        if (this.wizDatasource.selectionMode !== 'none' && !this.gridOptions.suppressRowClickSelection) {
          value.event.preventDefault();
          // force le selected à false
          // TODO LMA : pourquoi ce morceau de code ?
          value.node.setSelected(false); // on force la déselection ? pour donner la main à la DS.. .pour gérer le changing ?
          this.synchronizeSelectionState();
          // dispatch du changing, pour éventuel preventDefault
          WizEventManager.dispatchEvent(new ElementRef(value.event.target),  new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGING, this, value.data));
        }

        WizEventManager.dispatchEvent(new ElementRef(value.event.target), new WizEvent(WIZ_EVENT_TYPE.GRID_ROW_CLICKED, this, value.data) );
      })
    );

    this._sub$.add( // changement du pool de sélection dans le grid
      this.selectionChanged.asObservable().subscribe((evt: SelectionChangedEvent) => {
       
        const nodes: any[] = this.api.getSelectedNodes()?.map((node) => node.data);    
        // console.log('selectionChanged', evt, nodes); 
        const dsSelect: any[] = this.wizDatasource.selectedItems || [];
        if (!this._fixMeImTheSame(dsSelect, nodes)) {
          this.wizDatasource.setSelectedItems(nodes);
          // console.log('aggrid selectionChanged', this.wizDatasource.selectedItems);
          WizEventManager.dispatchEvent(this, new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGED, this, this.wizDatasource.selectedItems));
        }
      })
    );

    this._sub$.add( // détection d'une sélection par colonne de check, pour mode singleSelect
      this.rowSelected.asObservable().subscribe((evt: RowSelectedEvent) => {  
        const doSelect: boolean = evt.node.isSelected();
        if(this.wizRowSelection === 'wiz-single') {  
          // console.log('rowSelected', evt.node.id, doSelect);
          evt.node.setSelected(doSelect, true, false); // on force le clear à true
        }
      })
    );

  }

  /** l'écouteur sur selectionChanged renvoie trop d'events, pour rien */
  private _fixMeImTheSame(dsSelect: any[], nodes: any[] ): boolean {
    // console.log('_fixMeImTheSame', dsSelect, nodes);
    if (dsSelect?.length !== nodes?.length) {
      return false;
    }

    let compareColl: any[] = dsSelect.filter(o1 => nodes.some(o2 => o1 === o2));
    // console.log('compareColl', compareColl);
    if (compareColl?.length !== dsSelect?.length) {
      return false;
    }
    
    return true;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._sub$.unsubscribe();
    WizEventManager.removeListener(this, WizRoot.root(), WIZ_EVENT_TYPE.TRANSLATION_LOADED);
    WizEventManager.removeListener(this, this, WIZ_EVENT_TYPE.SELECTION_CHANGING);
    WizEventManager.removeListener(this, this, WIZ_EVENT_TYPE.ROW_GROUP_EXPAND);
  }

  /**
   * Méthode appelée pour cancel l'event de sélection d'une ligne avec la barre espace
   * @param params Les paramètres contenant les informations sur l'event
   */
  private shouldSuppressKeyboardevent(params: SuppressKeyboardEventParams): boolean {
    if (
      (this.rowSelection === 'single' || this.rowSelection === 'multiple'|| this.rowSelection === 'wiz-single') &&
      !params.editing &&
      params.event.key === VALUE_SPACE &&
      !this.wizDisabled
    ) {
      // Si on a un mode de sélection actif et si on est pas en train d'éditer
      // et qu'on a appuyé sur espace, on dispatch l'event de selection changing
      params.event.preventDefault();
      params.event.stopPropagation();
      WizEventManager.dispatchEvent(
        new ElementRef(params.event.target),
        new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGING, this, params.data)
      );

      // On empêche le comportement par défaut
      return true;
    }

    return false;
  }

  private onSelectionChanging(pEvent: WizEvent): void {
    if (!pEvent.defaultPrevented && !this.wizDisabled) {
      this.wizDatasource.toggle(pEvent.value);
    }
  }

  /**
   * Ecouteur de chargement des traductions pour rafraichir les headers
   */
  private onTranslationLoaded(_: WizEvent): void {
    if (this._initialized) {
      this.api.refreshHeader();
    }
  }

  /**
   * Met à jour les colonnes pour leur assigner le getter permettant d'effectuer la traduction dans les headers
   * @param columns la définition des colonnes
   */
  private setHeaderValueGetter(columns: any[]): void {
    columns.forEach((c) => {
      if (!c.headerValueGetter) {
        c.headerValueGetter = this.translatedHeaderValueGetter;

        // appel en récursif pour les enfants. Cela permet la gestion des groupes
        if (!!c.children) {
          this.setHeaderValueGetter(c.children);
        }
      }
    });
  }

  /**
   * Getter appelé par ag-grid pour donner la valeur de traduction de l'entête
   * @param params Les paramètres d'appel (contient la définition de la colonne, l'api...)
   */
  private translatedHeaderValueGetter(params: any): string {
    if (params.colDef && params.location === 'header' && !!params.colDef.headerName) {
      return WizTranslationManager.getInstance().translateKey(params.colDef.headerName);
    }
    return '';
  }

  // TODO implémentation IWizComponent
  entityUID: string;
  wizMandatory: boolean;

  protected _wizDisabled: boolean = false;

  @Input()
  public set wizDisabled(v: boolean) {
    this.setWizDisabled(v);
  }

  public get wizDisabled(): boolean {
    return this.getWizDisabled();
  }

  // accesseurs mode Java pour override dans les classes héritées
  public setWizDisabled(v: boolean) {
    this._wizDisabled = v;

    if (this._dndConfig) {
      this._dndConfig.dragEnabled = !this.wizDisabled;
      this._dndConfig.dropEnabled = !this.wizDisabled;

      this.handleDragColumns();
    }

    this.rowSelection = 'none';

    if (!!this.gridOptions) {
      this.gridOptions.rowSelection = 'none';
    }

    this.synchronizeDisabledOnColumns(true);
  }

  public getWizDisabled(): boolean {
    return this._wizDisabled;
  }

  public synchronizeDisabledOnColumns(pRefreshDs: boolean = false) {
    if (!!this.columnApi) {
      this.columnApi.getAllColumns().forEach((col: Column) => {
        if (!col.getColDef().cellRendererParams) {
          col.getColDef().cellRendererParams = {};
        }
        col.getColDef().cellRendererParams.wizDisabled = this._wizDisabled;
      });

      if (pRefreshDs) {
        this.wizDatasource.refresh();
      }
    }
  }

  wizLabel: string;
  id: string;



  
  /** les lignes cachées, par leur key = la ligne maîtresse */
  @Input()
  hiddenRows: Map<any, any>;

  /**
   * expandAll depuis le header de la colonne rowGroup
   * @param resetMode, true si reconstruction des lignes. Dans ce cas, inutile de refresh les rowGroups
   */
  public expandAllRows(resetMode: boolean = false): void {
    // console.log('refreshHidden', this.hiddenRows);
    if(resetMode) {
      this.hiddenRows = null;
    } else {
      [...this.hiddenRows?.values()].forEach(widgetLine => {
        // TODO TODO collecter les Node pour refresh
        // on repasse les lignes à expand
        widgetLine.subTotalExpended = true;
      }); 
      
      this.hiddenRows = null;
      this._onDataSourceRefreshed();
      WizEventManager.dispatchEvent(this, new WizEvent(WIZ_EVENT_TYPE.ROW_GROUP_CHANGED, this, this.hiddenRows));  
    }

  }

  /**
   * modification externe des rowGroup (clic au niveau du groupe)
   * le hiddenRows est déjkà alimenté
   */
  public refreshHiddenRows(): void {
    // console.log('refreshHidden', this.hiddenRows);
    this._onDataSourceRefreshed();
    WizEventManager.dispatchEvent(this, new WizEvent(WIZ_EVENT_TYPE.ROW_GROUP_CHANGED, this, this.hiddenRows));
 
  }

  /**
   * filtre des lignes de détail
   * @param item WidgetLine
   * @returns true si la ligne n'est pas masquée par son groupe parent
   */
  public isIN(item : any) : boolean {
    let res = true;
    if(this.hiddenRows?.has(item)) {
      res = false;
    }
    return res;
  }

  @Output()
  collapseOrExpandGroups: EventEmitter<WizEvent> = new EventEmitter();

  /**
   * clic sur le header des RowGroup
   * @param ev, ev.value si true (== expand), on supprime toutes les hiddenRows
   */
  private onGroupHeaderExpand(ev: WizEvent): void {
    this.collapseOrExpandGroups.emit(ev);
  }

  @Input()
  undisableable: boolean = false;
}
